'use client'

import React, { useEffect, useRef } from 'react'
import gsap from 'gsap'

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
}

/**
 * Botão "Liquid Glass" — a ação principal, na cor da marca.
 *
 * Vidro fosco com uma onda da cor da marca borrada se movendo dentro, e
 * elevação no hover. Toda cor sai de `--gr-accent` (derivado de `--gr-hue`),
 * então ele repinta junto com a instalação — nada de hex cravado.
 *
 * # Por que ele deixou de ser só do "bem-vindo de volta"
 * Até 14/09/2026 ele aparecia numa situação só: quando a pessoa JÁ tinha sessão
 * e a tela oferecia o atalho de reentrar. O caminho normal — e-mail, senha,
 * Entrar — usava um retângulo chapado. Dois botões "Entrar" na mesma tela, com
 * dois desenhos. O dono viu e perguntou se não dava para ser sempre assim.
 * Dava, e passou a ser.
 *
 * # Três coisas que precisaram existir antes
 * Ele nasceu para UMA instância numa tela estática. Para virar a ação principal
 * de um formulário, faltava:
 *
 *  1. **DESATIVADO.** O botão de um formulário fica desativado enquanto falta
 *     campo. Sem estado desativado ele parecia clicável e não era — e o vidro,
 *     que é translúcido, não sabia ficar apagado.
 *  2. **QUIETO quando a pessoa pede.** A onda é uma animação infinita. Com
 *     `prefers-reduced-motion: reduce` ela não roda: quem pediu para o sistema
 *     reduzir movimento não deve receber movimento perpétuo na tela de entrada.
 *     Também não roda desativado — animar o que não responde é mentira visual.
 *  3. **UM `<style>` só.** Cada instância emitia a própria cópia do CSS. Numa
 *     tela era invisível; em dez, são dez blocos idênticos no DOM. O CSS subiu
 *     para uma constante emitida uma vez por página, com o React deduplicando
 *     pelo conteúdo.
 *
 * ⚠️ O que ele AINDA não é: o `.gr-btn--primary` do produto inteiro. O texto é
 * escuro sobre vidro claro, então ele conta com fundo CLARO atrás — em superfície
 * escura ou dentro de um modal com véu, o contraste cai. E o embrulho centraliza
 * (`margin: 0 auto`), o que reposicionaria botão de barra de ferramentas. Promover
 * de vez pede uma variante escura e um embrulho que não centraliza. Está aqui
 * escrito para quem for fazer isso não descobrir na tela.
 */

/** Emitido UMA vez por página: o React deduplica `<style>` de conteúdo igual. */
const CSS = `
  .lgb-wrap {
    position: relative; align-self: center; width: fit-content; max-width: 100%;
    margin: 0 auto; border-radius: 9999px;
    box-shadow: 0 9px 22px color-mix(in oklch, var(--gr-accent) 22%, transparent);
    transition: box-shadow .25s ease;
  }
  .lgb {
    position: relative; z-index: 1; overflow: hidden; border: 0; outline: none;
    cursor: pointer; border-radius: 9999px; padding: 11px 22px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    font-size: 13px; font-weight: 600; letter-spacing: 0.01em; white-space: nowrap;
    font-family: inherit;
    color: var(--gr-accent-hover); text-shadow: 0 1px 0 rgba(255,255,255,0.55);
    background: linear-gradient(180deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.06) 60%, rgba(255,255,255,0.02) 100%);
    -webkit-backdrop-filter: blur(13px) saturate(1.25); backdrop-filter: blur(13px) saturate(1.25);
    box-shadow: inset 0 1px 1.5px rgba(255,255,255,0.8), inset 0 -1px 2px color-mix(in oklch, var(--gr-accent) 18%, transparent);
    transition: transform .12s ease;
  }
  .lgb-wave {
    position: absolute; z-index: -1; left: 14%; top: 10%; width: 70%; height: 105%;
    background: radial-gradient(circle at 50% 60%, color-mix(in oklch, var(--gr-accent) 60%, transparent), color-mix(in oklch, var(--gr-accent) 18%, transparent) 48%, transparent 72%);
    filter: blur(11px); pointer-events: none; will-change: transform, opacity;
  }
  .lgb-wrap:has(.lgb:hover:not(:disabled)) { box-shadow: 0 22px 38px color-mix(in oklch, var(--gr-accent) 34%, transparent); }
  .lgb-wrap:has(.lgb:focus-visible) { box-shadow: 0 0 0 3px color-mix(in oklch, var(--gr-accent) 42%, transparent), 0 9px 22px color-mix(in oklch, var(--gr-accent) 22%, transparent); }

  /* Desativado: o vidro apaga junto com a sombra que o levantava. Sem apagar a
     sombra, ele continuaria parecendo em relevo — e relevo é o que diz "clique". */
  .lgb:disabled { cursor: not-allowed; opacity: .55; }
  .lgb-wrap:has(.lgb:disabled) { box-shadow: none; }
  .lgb:disabled .lgb-wave { opacity: .25 !important; }

  @media (prefers-reduced-motion: reduce) {
    .lgb-wrap, .lgb { transition: none; }
  }
`

export default function LiquidGlassButton({ children, className = '', disabled, ...props }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    // Nem quem pediu menos movimento, nem botão que não responde ao clique.
    if (disabled) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const wave = wrap.querySelector<HTMLElement>('.lgb-wave')
    if (!wave) return
    const ctx = gsap.context(() => {
      gsap.to(wave, { xPercent: 28, yPercent: -10, scale: 1.22, duration: 3, ease: 'sine.inOut', yoyo: true, repeat: -1 })
      gsap.to(wave, { opacity: 0.62, duration: 2.2, ease: 'sine.inOut', yoyo: true, repeat: -1 })
    }, wrap)
    return () => ctx.revert()
  }, [disabled])

  const lift = (y: number, duration: number) => {
    if (disabled) return
    if (wrapRef.current) gsap.to(wrapRef.current, { y, duration, ease: 'power3.out', overwrite: 'auto' })
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="lgb-wrap" ref={wrapRef} onMouseEnter={() => lift(-6, 0.32)} onMouseLeave={() => lift(0, 0.42)}>
        <button className={`lgb ${className}`} disabled={disabled} {...props}>
          <span
            className="lgb-wave"
            aria-hidden="true"
            style={{ transform: 'translate(14%, -5%) scale(1.11)', opacity: 0.55 }}
          />
          {children}
        </button>
      </div>
    </>
  )
}
